import type { Idl, Program } from '@coral-xyz/anchor';
import {
  type AccountInfo,
  type Connection,
  type PublicKey,
  SYSVAR_CLOCK_PUBKEY,
} from '@solana/web3.js';
import BN from 'bn.js';
import { CP_SWAP_PROGRAM_ID, MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getCpSwapProgram, getProgram } from '../idl/loader.js';
import { deriveAmmPoolStatePda, sortMintPair } from '../pda/cp-swap.js';
import { decodeChainClock } from './chain-clock.js';
import type { AmmConfigAccount, PoolStateAccount } from './cp-swap-types.js';
import {
  rejectInvalidObservationAccount,
  rejectPoolIdentityChange,
  requireCanonicalAmmConfigAddress,
  requireCanonicalCpSwapProgram,
  requireObservationPoolBinding,
  samePoolIdentity,
  validateAtomicTokenAccounts,
  validateCanonicalAmmConfig,
  validateCanonicalPoolIdentity,
} from './cp-swap-validation.js';
import { graduatedMarketPoolBinding } from './market-pool-binding.js';
import type {
  ConvictionMarketConfigAccount,
  GiftAccount,
  GiftConfigAccount,
  MarketAccount,
  TimedMarketConfigAccount,
} from './types.js';

interface AccountNamespaceEntry {
  // ALLOW-UNKNOWN: Anchor's account namespace returns raw IDL-typed shapes that callers narrow per account.
  fetch(address: PublicKey): Promise<unknown>;
  // ALLOW-UNKNOWN: Anchor's account namespace returns raw IDL-typed shapes that callers narrow per account.
  fetchMultiple(addresses: Array<PublicKey>): Promise<Array<unknown | null>>;
  // ALLOW-UNKNOWN: Anchor's account namespace returns raw IDL-typed shapes that callers narrow per account.
  all(): Promise<Array<{ publicKey: PublicKey; account: unknown }>>;
}

type AccountsRecord = Record<string, AccountNamespaceEntry | undefined>;

function getAccountEntry(program: Program<Idl>, name: string): AccountNamespaceEntry {
  const entry = (program.account as AccountsRecord)[name];
  if (entry === undefined) {
    throw new Error(`Account type "${name}" not found in program IDL`);
  }
  return entry;
}

async function getV2Program(connection: Connection, programId: PublicKey): Promise<Program<Idl>> {
  return getProgram(connection, programId);
}

export async function fetchMarket(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<MarketAccount> {
  const program = await getV2Program(connection, programId);
  return (await getAccountEntry(program, 'market').fetch(address)) as MarketAccount;
}

export async function fetchMultipleMarkets(
  connection: Connection,
  addresses: Array<PublicKey>,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<Array<MarketAccount | null>> {
  const program = await getV2Program(connection, programId);
  return (await getAccountEntry(program, 'market').fetchMultiple(
    addresses,
  )) as Array<MarketAccount | null>;
}

export async function fetchAllMarkets(
  connection: Connection,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<Array<{ publicKey: PublicKey; account: MarketAccount }>> {
  const program = await getV2Program(connection, programId);
  return (await getAccountEntry(program, 'market').all()) as Array<{
    publicKey: PublicKey;
    account: MarketAccount;
  }>;
}

export async function fetchMarketConfig(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<ConvictionMarketConfigAccount | TimedMarketConfigAccount> {
  const program = await getV2Program(connection, programId);
  const info = await connection.getAccountInfo(address);
  if (info === null || !info.owner.equals(program.programId)) {
    throw new Error('Market configuration is missing or has an unexpected owner');
  }
  const names = ['convictionMarketConfig', 'timedMarketConfig'] as const;
  for (const name of names) {
    const discriminator = program.idl.accounts?.find(
      (account) => account.name === name,
    )?.discriminator;
    if (
      discriminator !== undefined &&
      Buffer.from(discriminator).equals(info.data.subarray(0, discriminator.length))
    ) {
      return program.coder.accounts.decode<
        ConvictionMarketConfigAccount | TimedMarketConfigAccount
      >(name, info.data);
    }
  }
  throw new Error('Account is not a current market configuration');
}

export async function fetchGiftConfig(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<GiftConfigAccount> {
  const program = await getV2Program(connection, programId);
  return (await getAccountEntry(program, 'giftConfig').fetch(address)) as GiftConfigAccount;
}

export async function fetchGift(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<GiftAccount> {
  const program = await getV2Program(connection, programId);
  return (await getAccountEntry(program, 'gift').fetch(address)) as GiftAccount;
}

/**
 * Fetch a graduated market's cp-swap `AmmConfig` (carries the pool fee rates).
 * `address` is the protected config recorded by the canonical CP PoolState.
 */
export async function fetchAmmConfig(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey = CP_SWAP_PROGRAM_ID,
): Promise<AmmConfigAccount> {
  const program = await getCpSwapProgram(connection, programId);
  return (await getAccountEntry(program, 'ammConfig').fetch(address)) as AmmConfigAccount;
}

/**
 * Fetch a graduated market's cp-swap `PoolState`. Reserves are NOT on this
 * account — read the SPL balances of `token0Vault`/`token1Vault` separately.
 */
export async function fetchPoolState(
  connection: Connection,
  address: PublicKey,
  programId: PublicKey = CP_SWAP_PROGRAM_ID,
): Promise<PoolStateAccount> {
  const program = await getCpSwapProgram(connection, programId);
  return (await getAccountEntry(program, 'poolState').fetch(address)) as PoolStateAccount;
}

/** Live trading inputs for a graduated outcome's cp-swap pool. */
export interface GraduatedPoolInfo {
  /** cp-swap pool state PDA. */
  poolState: PublicKey;
  /** Program that owns and executes this exact pool. */
  ammProgramId: PublicKey;
  /** AmmConfig recorded by the PoolState. */
  ammConfig: PublicKey;
  /** Active-network USDC quote mint required by the caller and validated against the pool. */
  quoteMint: PublicKey;
  outcomeMint: PublicKey;
  token0Vault: PublicKey;
  token1Vault: PublicKey;
  token0Program: PublicKey;
  token1Program: PublicKey;
  observationState: PublicKey;
  /** Quote side (D6), net of accrued protocol/fund/creator liabilities. */
  usdcReserves: BN;
  /** Outcome side (D6), net of accrued protocol/fund/creator liabilities. */
  outcomeReserves: BN;
  /** Exact cp-swap rates; all four use the contract's 1_000_000 denominator. */
  tradeFeeRateMillionths: BN;
  protocolFeeRateMillionths: BN;
  fundFeeRateMillionths: BN;
  creatorFeeRateMillionths: BN;
  /** 0 = both/input token, 1 = token0 only, 2 = token1 only. */
  creatorFeeOn: number;
  creatorFeeEnabled: boolean;
  /** Bit-2 pool status gate, already normalized for quote callers. */
  swapEnabled: boolean;
  /** Unix timestamp before which the pool rejects swaps. */
  openTime: BN;
  /** Which sorted CP token side the quote mint occupies. */
  quoteTokenIndex: 0 | 1;
  /** Solana Clock observed atomically with the mutable CP pool state. */
  chainSlot: BN;
  chainUnixTimestampSeconds: BN;
  /**
   * Compatibility-only rounded BPS value. Canonical quote math must use
   * `tradeFeeRateMillionths`; sub-BPS rates cannot round-trip through this
   * field.
   *
   * @deprecated Use `tradeFeeRateMillionths`.
   */
  lpFeeBps: number;
}

/**
 * Resolve the live trading inputs for one graduated outcome's cp-swap pool:
 * the pool PDA, both effective reserves (D6), exact fee configuration, and
 * pool gates. Derives the pool PDA deterministically from the AmmConfig +
 * sorted mint pair, then uses the decoded PoolState vaults and subtracts every
 * accrued fee liability exactly as the on-chain swap instruction does.
 */
export async function fetchGraduatedPoolInfo(
  connection: Connection,
  params: {
    ammConfig: PublicKey;
    ammProgramId: PublicKey;
    quoteMint: PublicKey;
    outcomeMint: PublicKey;
  },
): Promise<GraduatedPoolInfo> {
  requireCanonicalCpSwapProgram(params.ammProgramId);
  requireCanonicalAmmConfigAddress(params.ammConfig);
  const [mint0, mint1] = sortMintPair(params.quoteMint, params.outcomeMint);
  const poolState = deriveAmmPoolStatePda(params.ammConfig, mint0, mint1, params.ammProgramId);
  return fetchGraduatedPoolInfoAtAddress(connection, {
    poolState,
    expectedQuoteMint: params.quoteMint,
    expectedOutcomeMint: params.outcomeMint,
  });
}

/**
 * Resolve a migrated pool against the canonical winner on the permanent Market account.
 */
export async function fetchGraduatedPoolInfoByPool(
  connection: Connection,
  params: {
    poolState: PublicKey;
    marketAddress: PublicKey;
    market: MarketAccount;
    expectedQuoteMint: PublicKey;
    minimumContextSlot?: number;
  },
): Promise<GraduatedPoolInfo> {
  const binding = graduatedMarketPoolBinding(params.marketAddress, params.market);
  if (!binding.poolState.equals(params.poolState))
    throw new Error('cp-swap pool does not match the Market winner');
  if (!binding.quoteMint.equals(params.expectedQuoteMint))
    throw new Error('Market quote mint does not match the expected quote mint');
  const minimumContextConfig =
    params.minimumContextSlot === undefined
      ? undefined
      : { minContextSlot: params.minimumContextSlot };
  const poolInfo = await connection.getAccountInfo(params.poolState, minimumContextConfig);
  if (poolInfo === null) {
    throw new Error(`cp-swap PoolState ${params.poolState.toBase58()} is missing`);
  }
  requireCanonicalCpSwapProgram(poolInfo.owner);
  return fetchGraduatedPoolInfoAtAddress(connection, {
    poolState: params.poolState,
    discoveredPoolInfo: poolInfo,
    expectedQuoteMint: params.expectedQuoteMint,
    expectedOutcomeMint: binding.outcomeMint,
    ...(params.minimumContextSlot === undefined
      ? {}
      : { minimumContextSlot: params.minimumContextSlot }),
  });
}

interface GraduatedPoolFetchInput {
  poolState: PublicKey;
  discoveredPoolInfo?: AccountInfo<Buffer>;
  expectedQuoteMint: PublicKey;
  expectedOutcomeMint: PublicKey;
  minimumContextSlot?: number;
}

interface ObservationStateAccount {
  poolId: PublicKey;
}

async function fetchGraduatedPoolInfoAtAddress(
  connection: Connection,
  input: GraduatedPoolFetchInput,
): Promise<GraduatedPoolInfo> {
  const program = await getCpSwapProgram(connection, CP_SWAP_PROGRAM_ID);
  const discoveredPool = input.discoveredPoolInfo
    ? decodeProgramAccount<PoolStateAccount>(
        program,
        'poolState',
        input.poolState,
        input.discoveredPoolInfo,
      )
    : ((await getAccountEntry(program, 'poolState').fetch(input.poolState)) as PoolStateAccount);
  validateCanonicalPoolIdentity({
    poolState: input.poolState,
    pool: discoveredPool,
    expectedQuoteMint: input.expectedQuoteMint,
    expectedOutcomeMint: input.expectedOutcomeMint,
  });

  // Refresh every authoritative swap account and Clock in one RPC context so
  // a concurrent swap cannot mix pre/post-swap state.
  const batch = await connection.getMultipleAccountsInfoAndContext(
    [
      discoveredPool.ammConfig,
      input.poolState,
      discoveredPool.token0Vault,
      discoveredPool.token1Vault,
      discoveredPool.token0Mint,
      discoveredPool.token1Mint,
      discoveredPool.observationKey,
      SYSVAR_CLOCK_PUBKEY,
    ],
    input.minimumContextSlot === undefined
      ? undefined
      : { minContextSlot: input.minimumContextSlot },
  );
  const ammConfig = decodeProgramAccount<AmmConfigAccount>(
    program,
    'ammConfig',
    discoveredPool.ammConfig,
    accountInfoAt(batch.value, 0, 'cp-swap AmmConfig'),
  );
  validateCanonicalAmmConfig(discoveredPool.ammConfig, ammConfig);
  const pool = decodeProgramAccount<PoolStateAccount>(
    program,
    'poolState',
    input.poolState,
    accountInfoAt(batch.value, 1, 'cp-swap PoolState'),
  );
  if (!samePoolIdentity(pool, discoveredPool)) {
    rejectPoolIdentityChange();
  }
  const { quoteTokenIndex, outcomeTokenIndex } = validateCanonicalPoolIdentity({
    poolState: input.poolState,
    pool,
    expectedQuoteMint: input.expectedQuoteMint,
    expectedOutcomeMint: input.expectedOutcomeMint,
  });
  const { token0: token0Account, token1: token1Account } = validateAtomicTokenAccounts({
    pool,
    token0VaultInfo: accountInfoAt(batch.value, 2, 'cp-swap token0 vault'),
    token1VaultInfo: accountInfoAt(batch.value, 3, 'cp-swap token1 vault'),
    token0MintInfo: accountInfoAt(batch.value, 4, 'cp-swap token0 mint'),
    token1MintInfo: accountInfoAt(batch.value, 5, 'cp-swap token1 mint'),
  });
  const observation = decodeObservationAccount(
    program,
    accountInfoAt(batch.value, 6, 'cp-swap ObservationState'),
  );
  requireObservationPoolBinding(input.poolState, observation.poolId);
  const chainClock = decodeChainClock(accountInfoAt(batch.value, 7, 'Solana Clock sysvar'));
  const token0Effective = effectiveVaultBalance(
    new BN(token0Account.amount.toString()),
    pool.protocolFeesToken0,
    pool.fundFeesToken0,
    pool.creatorFeesToken0,
  );
  const token1Effective = effectiveVaultBalance(
    new BN(token1Account.amount.toString()),
    pool.protocolFeesToken1,
    pool.fundFeesToken1,
    pool.creatorFeesToken1,
  );

  return {
    poolState: input.poolState,
    ammProgramId: CP_SWAP_PROGRAM_ID,
    ammConfig: pool.ammConfig,
    quoteMint: input.expectedQuoteMint,
    outcomeMint: input.expectedOutcomeMint,
    token0Vault: pool.token0Vault,
    token1Vault: pool.token1Vault,
    token0Program: pool.token0Program,
    token1Program: pool.token1Program,
    observationState: pool.observationKey,
    usdcReserves: quoteTokenIndex === 0 ? token0Effective : token1Effective,
    outcomeReserves: outcomeTokenIndex === 0 ? token0Effective : token1Effective,
    tradeFeeRateMillionths: ammConfig.tradeFeeRate,
    protocolFeeRateMillionths: ammConfig.protocolFeeRate,
    fundFeeRateMillionths: ammConfig.fundFeeRate,
    creatorFeeRateMillionths: ammConfig.creatorFeeRate,
    creatorFeeOn: pool.creatorFeeOn,
    creatorFeeEnabled: pool.enableCreatorFee,
    swapEnabled: (pool.status & 4) === 0,
    openTime: pool.openTime,
    quoteTokenIndex,
    chainSlot: new BN(chainClock.slot.toString()),
    chainUnixTimestampSeconds: new BN(chainClock.unixTimestampSeconds.toString()),
    lpFeeBps: Math.round(ammConfig.tradeFeeRate.toNumber() / 100),
  };
}

function accountInfoAt(
  accounts: ReadonlyArray<AccountInfo<Buffer> | null>,
  index: number,
  label: string,
): AccountInfo<Buffer> {
  const account = accounts[index];
  if (account === undefined || account === null) {
    throw new Error(`${label} is missing from atomic snapshot`);
  }
  return account;
}

function decodeProgramAccount<T>(
  program: Program<Idl>,
  accountName: string,
  address: PublicKey,
  info: AccountInfo<Buffer>,
): T {
  if (!info.owner.equals(program.programId)) {
    throw new Error(`${accountName} ${address.toBase58()} has an unexpected owner`);
  }
  return program.coder.accounts.decode<T>(accountName, info.data);
}

function decodeObservationAccount(
  program: Program<Idl>,
  info: AccountInfo<Buffer>,
): ObservationStateAccount {
  requireCanonicalCpSwapProgram(info.owner);
  try {
    return program.coder.accounts.decode<ObservationStateAccount>('observationState', info.data);
  } catch {
    return rejectInvalidObservationAccount();
  }
}

function effectiveVaultBalance(raw: BN, protocol: BN, fund: BN, creator: BN): BN {
  const liabilities = protocol.add(fund).add(creator);
  if (raw.lt(liabilities)) {
    throw new Error('cp-swap fee liabilities exceed physical vault balance');
  }
  return raw.sub(liabilities);
}
